import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import {
	IInstrumentation,
	InstrumentationService,
	InstrumentEnum
} from 'src/app/core/services/instrumentation/instrumentation.service';
import IProduct from 'src/app/core/services/loan-application/product-offer/product/product.model';
import { TealiumService } from 'src/app/vendor/tealium/tealium.service';

import { Environment, EnvironmentService } from '../environment/environment.service';
import { ApplicantUtils } from '../loan-application/applicant/applicant-utils';
import { ILoanApplication } from '../loan-application/loan-application.model';
import { LoanApplicationService } from '../loan-application/loan-application.service';
import { RoutingHistoryService } from '../routing/routing-history.service';

@Injectable({
	providedIn: 'root'
})
export class TagDataService implements OnDestroy {
	tagData = {
		application_type: 'CONSUMER_INSTALLMENT_LOAN'
	};
	instrumentation: IInstrumentation = {} as IInstrumentation;

	private subscription = new Subscription();

	constructor(
		private environmentService: EnvironmentService,
		private loanAppService: LoanApplicationService,
		private tealiumService: TealiumService,
		private routingHistoryService: RoutingHistoryService,
		private instrumentationService: InstrumentationService
	) {
		const loanAppSub = this.loanAppService.loanApplication$.subscribe({
			next: (loanApp) => {
				this.updateLoanAppDataForTracking(loanApp);
			}
		});
		this.subscription.add(loanAppSub);

		const instrumentationSub = this.instrumentationService.instrumentation$.subscribe({
			next: (instrumentation) => {
				this.instrumentation = instrumentation;
			}
		});
		this.subscription.add(instrumentationSub);

		this.setLanguage();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	initConfig() {
		this.tealiumService.setConfig({
			account: 'oportun',
			profile: 'main',
			environment: this.getTealiumEnvironment()
		});
	}

	gatherInstrumentation(): any {
		return Object.keys(InstrumentEnum).reduce((acc, key) => {
			const keyData = this.instrumentation[key];
			if (keyData) {
				acc[key] = keyData;
			}
			return acc;
		}, {});
	}

	merge(data: any): void {
		this.tagData = {
			...this.tagData,
			...data
		};
	}

	/**
	 *	The view() function tracks page views, virtual page views, and Ajax page flows.
	 * Calling this function triggers the corresponding page tracking functionality within your configured vendor tags.
	 * utag.js automatically triggers this function on every page load.
	 *
	 * @param {*} [viewData]
	 * @param {*} [obj]
	 * @memberof TagDataService
	 */
	view(viewData?: any, obj?: any): void {
		this.merge(viewData);
		let data = { ...this.tagData, ...this.gatherInstrumentation() };
		if (obj) {
			data = {
				...data,
				...obj
			};
		}
		// Add non application data like the current and previous page
		this.updatePathDataForTracking();
		this.tealiumService.view(data);
	}

	/**
	 * The link() function tracks events such as non-page views, page interactions, and other dynamic page events.
	 * Event tracking collects information about your visitors’ interactions within a page.
	 * While page view tracking gathers information about the navigation path a visitor takes, event tracking monitors what visitors do in-between navigation steps.
	 *
	 * @param {*} [linkData]
	 * @param {*} [event]
	 * @memberof TagDataService
	 */
	link(linkData?: any, event?: any): void {
		this.merge(linkData);
		let data = { ...this.tagData, ...this.gatherInstrumentation() };
		if (event) {
			data = {
				...data,
				...event
			};
		}
		// Add non application data like the current and previous page
		this.updatePathDataForTracking();
		this.tealiumService.link(data);
	}

	reset(): void {
		this.tagData = {
			application_type: 'CONSUMER_INSTALLMENT_LOAN'
		};
	}

	setLanguage(): void {
		this.link({
			event_action: 'submit',
			event_category: 'Form',
			event_form: 'rx_form',
			tealium_event: 'change_language',
			application_type: 'CONSUMER_INSTALLMENT_LOAN',
			event_source: 'online-origination'
		});
	}

	getTealiumEnvironment(): string {
		const envMap = {
			[Environment.Stage]: Environment.Qa,
			[Environment.Local]: Environment.Dev
		};
		return (envMap[this.environmentService.env] || this.environmentService.env).toLowerCase();
	}

	getTeliumVisitorId(): string {
		return this.tealiumService.getTeliumVisitorId();
	}

	getTealiumStringForOfferStatus(productOfferDetails: IProduct[]) {
		if (!productOfferDetails || productOfferDetails.length < 1) {
			return null;
		}
		return (
			productOfferDetails.length > 0 &&
			productOfferDetails
				.map((detail) => {
					return detail.productCategory + ':' + detail.productStatus;
				})
				.join(',')
		);
	}

	updateLoanAppDataForTracking(loanApp: ILoanApplication): void {
		const currentApplicant = ApplicantUtils.fromLoanApp(loanApp);
		this.merge({
			preferred_language: currentApplicant?.preferredLanguage,
			loan_application_id: loanApp?.id,
			application_status: loanApp?.applicationStatus,
			origination_source: loanApp?.originationSource,
			applicant_type: loanApp?.applicationType,
			applicant_id: currentApplicant?.id,
			created_at: loanApp?.createdAt,
			skip_references: currentApplicant?.skipReferences,
			referral_code: currentApplicant?.referralCode,
			referral_code_consent: currentApplicant?.referralCodeConsent,
			referral_code_source: currentApplicant?.referralCodeSource,
			previous_loan_count: currentApplicant?.previousLoanCount,
			clientId: currentApplicant?.clientId,
			issuing_org: loanApp?.issuingOrganization
		});
	}

	updatePathDataForTracking(): void {
		this.merge({
			application_type: 'CONSUMER_INSTALLMENT_LOAN',
			page_location: window.location.pathname,
			previous_page: this.routingHistoryService.getPreviousRoute(),
			current_page: this.routingHistoryService.getCurrentRoute()
		});
	}
}
